<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      max-width="650px"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Quest ({{ this.rules.name }})</span>
        <v-spacer/>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense class="mt-3">
          <v-col cols="12" md="12" sm="12" class="mb-n5">
            <v-text-field
                label="ID:"
                v-model.trim="model.id"
                v-ascii-only
                outlined
                dense
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="3" sm="3">
            <v-text-field label="Priority" v-model.number="model.priority" hide-details/>
          </v-col>
          <v-col cols="12" md="3" sm="3" class="mb-4">
            <v-checkbox v-model="model.master" label="Master" hide-details/>
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <r-quest-dropdown :model.sync="model.chainBack" label="Chain Back" :clearable="true"/>
          </v-col>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header v-slot="{ open }">
                <v-row no-gutters>
                  <span style="margin-top: 5px">Properties</span>
                  <v-spacer/>
                  <v-fade-transition>
                    <span style="display: inline-flex" v-if="open">
                      <v-divider class="mx-1" vertical/>
                    </span>
                  </v-fade-transition>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="6" sm="6">
                    <r-quest-show :model.sync="model.showOnAvailable" label="Show on available:"/>
                  </v-col>
                  <v-col cols="12" md="6" sm="6">
                    <r-quest-show :model.sync="model.showOnComplete" label="Show on complete:"/>
                  </v-col>
                  <v-col cols="12" md="6" sm="6">
                    <v-text-field label="Delay on available:" v-model.number="model.delayOnAvailable" hide-details/>
                  </v-col>
                  <v-col cols="12" md="6" sm="6">
                    <v-text-field label="Delay on complete:" v-model.number="model.delayOnComplete" hide-details/>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <r-reward-panel :model.sync="model.reward" title="Rewards"/>
            <r-guard-panel :model.sync="model.guard" title="Guard Config"/>
            <r-trigger-panel :items.sync="model.triggersAvailable" title="Triggers - Available"/>
            <r-trigger-panel :items.sync="model.triggersComplete" title="Triggers - Complete"/>
            <r-money-panel :model.sync="model.priceComplete" title="Price - Complete" :ticket="true"/>
            <r-panel title="Map Group">
              <r-map-group-dropdown :model.sync="model.mapGroup" />
            </r-panel>
            <r-panel title="Map Kind">
              <mapkind-dropdown v-model="model.mapKind" :clearable="true"/>
            </r-panel>
          </v-expansion-panels>
        </v-row>
      </v-card-text>
      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {QuestModel} from "@/modules/redactor/models/quest/QuestModel";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RTriggerPanel from "@/modules/redactor/views/trigger/r-trigger-panel";
import RRewardPanel from "@/modules/redactor/views/reward/r-reward-panel.vue";

import RQuestShow from "@/modules/redactor/views/quest/r-quest-show";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RMoneyPanel from "@/modules/redactor/views/currency/r-money-panel.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import RPanel from "@/modules/redactor/views/utils/r-panel.vue";
import RMapGroupDropdown from "@/modules/redactor/views/map-group/r-map-group-dropdown.vue";
import RQuestDropdown from "@/modules/redactor/views/quest/r-quest-dropdown.vue";
import {MapAddonGroupModel} from "@/modules/redactor/models/map-addon/core/MapAddonGroupModel";
import MapkindDropdown from "@/modules/mutils/views/map-save-action/mapkind-dropdown.vue";

export default {
  name: "r-quest-dialog",
  components: {RPreviewDialog, RMoneyPanel, RGuardPanel, RAuthorize, RQuestShow, RRewardPanel, RTriggerPanel, RPanel, RMapGroupDropdown, RQuestDropdown, MapkindDropdown},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(QuestModel),
    rules: ViewRules.READONLY,
    dialogWidth: 747,
    dialogHeight: 863,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 747,
    resizeStartHeight: 863,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new QuestModel();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject(this.model);
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 500;
      const minHeight = 360;
      const maxWidth = window.innerWidth - this.viewportPadding;
      const maxHeight = window.innerHeight - this.viewportPadding;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, QuestModel)
        .then((result) => {
          Object.assign(this.model, result);
        })
        .catch((e) => {
          this.$alert("Failed to parse config!" + e.message);
        });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>